import { ApolloClient } from '@apollo/client';
import gql from 'graphql-tag';

import {
  ActorTypes,
  AssignGrantReasons,
  PermissionActionClass} from '../../../src/rfxportal/pages/RFXRequest/steps/RFXRequestSuppliersStep/types';

/**
 * Assign Grant query for UMS
 */
export const assignGrantQuery = gql`
  query assignGrant($input: TS_AssignGrantRequest!) {
    assignGrant(input: $input) {
      processedPermissionActionClasses {
        permissionId
        actionClass
      }
    }
  }
`;

/**
 * Represents the result of the assignGrant query.
 *
 * @typedef {Object} AssignGrantResult
 * @property {PermissionActionClass[]} processedPermissionActionClasses - Array of processed permissions.
 */
export interface AssignGrantResult {
  processedPermissionActionClasses: PermissionActionClass[];
}

/**
 * Represents the parameters for the assignGrant function.
 *
 * @typedef {Object} AssignGrantParams
 * @property {ApolloClient<object>} client - Instance of ApolloClient used to execute query.
 * @property {string} principalId - ID of the principal the permissions will be granted to.
 * @property {string} actorType - Type of the principal (USER, GROUP).
 * @property {PermissionActionClass[]} permissions - Array of permissions to be granted.
 * @property {string} creator - ID of the actor (user or group) assigning the permissions.
 */
export interface AssignGrantParams {
  client: ApolloClient<object>;
  principalId: string;
  actorType: string;
  permissions: PermissionActionClass[];
  creator: string;
}

/**
 * Assigns permissions (grants) to a principal (user or group) in the User Management Service (UMS).
 *
 * @param {AssignGrantParams} params - Parameters for assigning grants.
 * @param {ApolloClient<object>} params.client - Instance of ApolloClient used to execute query.
 * @param {string} params.principalId - ID of the principal the permissions will be granted to.
 * @param {string} params.actorType - Type of the principal (USER, GROUP).
 * @param {PermissionActionClass[]} params.permissions - Array of permissions to be granted.
 * @param {string} params.creator - ID of the actor (user or group) assigning the permissions.
 * @returns {Promise<AssignGrantResult>} - Resolves with the AssignGrantResult object containing the processed permissions.
 */
export async function assignGrant({
  client,
  principalId,
  actorType,
  permissions,
  creator
}: AssignGrantParams): Promise<AssignGrantResult> {
  const variables = {
    input: {
      permissionActionClasses: permissions,
      principal: { id: principalId, type: actorType },
      assignedBy: { type: ActorTypes.USER, id: creator },
      reason: AssignGrantReasons.Assign
    }
  };
  const response = await client.query({
    query: assignGrantQuery,
    variables
  });
  return response.data.assignGrant;
}
